/**
 * Error hierarchy for Vesant SDK
 *
 * Provides structured error handling with specific error types
 * for different failure scenarios.
 */

export class VesantError extends Error {
  public requestId?: string;

  constructor(
    message: string,
    public code: string,
    public statusCode?: number,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'VesantError';
    Object.setPrototypeOf(this, VesantError.prototype);
  }
}

/** @deprecated Use VesantError instead */
export const CGSError = VesantError;
/** @deprecated Use VesantError instead */
export type CGSError = VesantError;

export class NetworkError extends VesantError {
  constructor(message: string, public originalError?: unknown) {
    super(message, 'NETWORK_ERROR');
    this.name = 'NetworkError';
    Object.setPrototypeOf(this, NetworkError.prototype);
  }
}

export class ValidationError extends VesantError {
  constructor(message: string, fields?: string[]) {
    super(message, 'VALIDATION_ERROR', 400, { fields });
    this.name = 'ValidationError';
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

export class ServiceUnavailableError extends VesantError {
  constructor(message: string = 'Service unavailable') {
    super(message, 'SERVICE_UNAVAILABLE', 503);
    this.name = 'ServiceUnavailableError';
    Object.setPrototypeOf(this, ServiceUnavailableError.prototype);
  }
}

export class ComplianceBlockedError extends VesantError {
  constructor(reasons: string[]) {
    super('Access blocked due to compliance rules', 'COMPLIANCE_BLOCKED', 403, { reasons });
    this.name = 'ComplianceBlockedError';
    Object.setPrototypeOf(this, ComplianceBlockedError.prototype);
  }
}

export class AuthenticationError extends VesantError {
  constructor(message: string = 'Authentication failed') {
    super(message, 'AUTHENTICATION_ERROR', 401);
    this.name = 'AuthenticationError';
    Object.setPrototypeOf(this, AuthenticationError.prototype);
  }
}

export class RateLimitError extends VesantError {
  constructor(public retryAfter?: number) {
    super('Rate limit exceeded', 'RATE_LIMIT_EXCEEDED', 429, { retryAfter });
    this.name = 'RateLimitError';
    Object.setPrototypeOf(this, RateLimitError.prototype);
  }
}

export class TimeoutError extends VesantError {
  constructor(public timeout: number) {
    super(`Request timeout after ${timeout}ms`, 'TIMEOUT', 408, { timeout });
    this.name = 'TimeoutError';
    Object.setPrototypeOf(this, TimeoutError.prototype);
  }
}

export class ComplianceError extends VesantError {
  constructor(
    message: string,
    public originalError?: unknown,
    code: string = 'COMPLIANCE_ERROR'
  ) {
    super(message, code);
    this.name = 'ComplianceError';
    Object.setPrototypeOf(this, ComplianceError.prototype);
  }
}

export class CircuitBreakerOpenError extends VesantError {
  constructor() {
    super('Circuit breaker is open — requests are temporarily blocked', 'CIRCUIT_BREAKER_OPEN', 503);
    this.name = 'CircuitBreakerOpenError';
    Object.setPrototypeOf(this, CircuitBreakerOpenError.prototype);
  }
}
